import { Check, ArrowUpRight, Wrench } from "lucide-react";
import { useLang } from "@/context";
import { SERVICE_IMAGES } from "@/i18n";
import { scrollToId } from "@/lib/scroll";
import { Reveal } from "./Reveal";

const ORDER = [
    { key: "ac", span: "lg:col-span-4", tall: true },
    { key: "fridge", span: "lg:col-span-2" },
    { key: "microwave", span: "lg:col-span-2" },
    { key: "washing", span: "lg:col-span-2" },
    { key: "geyser", span: "lg:col-span-2" },
    { key: "ro", span: "lg:col-span-4", tall: true },
];

export const Services = () => {
    const { t } = useLang();

    const book = (key) => {
        window.dispatchEvent(new CustomEvent("preselect-appliance", { detail: key }));
        scrollToId("book");
    };

    return (
        <section id="services" data-testid="services-section" className="py-20 sm:py-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Reveal className="max-w-2xl">
                    <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#8D1717]">{t.nav.services}</p>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#2A0505] mt-3">{t.services.heading}</h2>
                    <p className="text-base sm:text-lg text-[#6B5B57] mt-4">{t.services.sub}</p>
                </Reveal>

                <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-5 mt-12">
                    {ORDER.map((s, idx) => {
                        const item = t.services.items[s.key];
                        return (
                            <Reveal key={s.key} delay={idx * 0.05} className={s.span}>
                                <article
                                    data-testid={`service-card-${s.key}`}
                                    className="group h-full bg-white rounded-3xl border border-[#EADFD8] overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col"
                                >
                                    <div className={`relative overflow-hidden ${s.tall ? "h-52" : "h-40"}`}>
                                        <img
                                            src={SERVICE_IMAGES[s.key]}
                                            alt={item.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <p className="text-[11px] uppercase tracking-[0.2em] text-[#8D1717] font-bold">{item.tagline}</p>
                                        <h3 className="font-display text-xl sm:text-2xl font-bold text-[#2A0505] mt-1.5 leading-snug">{item.title}</h3>
                                        <p className="text-sm text-[#6B5B57] mt-2">{item.desc}</p>
                                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] mt-5 text-[#a08b85]">{t.services.commonProblems}</p>
                                        <ul className="mt-2.5 space-y-1.5 flex-1">
                                            {item.problems.map((p) => (
                                                <li key={p} className="flex gap-2 text-sm text-[#4a3b37]">
                                                    <Check className="w-4 h-4 text-[#8D1717] shrink-0 mt-0.5" />
                                                    {p}
                                                </li>
                                            ))}
                                        </ul>
                                        <button
                                            data-testid={`book-service-${s.key}`}
                                            onClick={() => book(s.key)}
                                            className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#8D1717] hover:gap-3 transition-all"
                                        >
                                            {t.services.book}
                                            <ArrowUpRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </article>
                            </Reveal>
                        );
                    })}

                    <Reveal delay={0.3} className="lg:col-span-2">
                        <div className="h-full rounded-3xl bg-[#8D1717] text-white p-7 flex flex-col justify-between shadow-lg shadow-[#8D1717]/25">
                            <div>
                                <span className="w-11 h-11 rounded-2xl bg-white/15 flex items-center justify-center">
                                    <Wrench className="w-5 h-5" />
                                </span>
                                <h3 className="font-display text-2xl font-bold mt-5 leading-snug">{t.services.ctaCard.title}</h3>
                                <p className="text-sm text-white/80 mt-3 leading-relaxed">{t.services.ctaCard.text}</p>
                            </div>
                            <button
                                data-testid="services-cta-card-button"
                                onClick={() => scrollToId("book")}
                                className="mt-6 inline-flex items-center justify-center gap-2 bg-white text-[#8D1717] font-bold text-sm rounded-full px-6 py-3.5 hover:bg-[#F3E6DE] transition-colors"
                            >
                                {t.services.ctaCard.button}
                                <ArrowUpRight className="w-4 h-4" />
                            </button>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
